/**
 * Cockpit's Help button panel: the shared `HelpCenter` from
 * `@ampersandbrands/ui` (help articles, "Report a problem with this page",
 * "Ask for something", and "My requests"), wired to the Service Desk api.
 *
 * This replaces the old Confluence help slide-out and the Jira "Report an
 * issue" dialog. The page context tells the desk where the person was (tool,
 * route, the filters on screen) and can grab a screenshot of the page.
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCenter, type HelpCenterView, type SdPageContext } from '@ampersandbrands/ui';
import { buildCurrentArticleLink } from '@/lib/articleLink';
import { cockpitServiceDeskClient } from '@/lib/serviceDeskClient';
import { getReportPageState } from '@/lib/reportPageState';
import { helpPageKeyForPath } from '@/lib/toolRouting';
import { captureCockpitScreenshot, cockpitToolLabelForPath } from '@/lib/supportIssue';

export function CockpitHelpCenter({
  open,
  onClose,
  onActivity,
  initialView,
}: {
  open: boolean;
  onClose: () => void;
  onActivity: () => void;
  /** Where the panel opens, e.g. one request from an emailed link. Defaults to the home view. */
  initialView?: HelpCenterView;
}) {
  const location = useLocation();

  // Read the page's published state at the moment the panel opens, so it
  // reflects what was on screen at the click and not wherever the page moves to.
  const page = useMemo<SdPageContext>(() => {
    const tool = cockpitToolLabelForPath(location.pathname);
    const state = open ? getReportPageState() : [];
    return {
      app: 'cockpit',
      page_key: helpPageKeyForPath(location.pathname),
      route: `${location.pathname}${location.search}`,
      url: typeof window === 'undefined' ? '' : window.location.href,
      page_title: typeof document === 'undefined' ? undefined : document.title,
      page_state: state.length > 0 ? state : undefined,
      tool_label: tool,
      captureScreenshot: () => captureCockpitScreenshot(tool),
    };
  }, [open, location.pathname, location.search]);

  return (
    <HelpCenter
      open={open}
      onClose={onClose}
      client={cockpitServiceDeskClient}
      page={page}
      audience="corp"
      initialView={initialView}
      onActivity={onActivity}
      buildArticleLink={buildCurrentArticleLink}
    />
  );
}
